import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { fetcher } from '@weezevent/weezjs-core';

import { Identity, Domain } from 'models';
import { usePermissions } from 'models/user/permissions';

import {
    Button,
    Dropdown,
    DropdownItem,
    DropdownSort,
    CopyToClipboardMinimal,
    FilterSetController,
    Filter,
    Card,
    ContentCard,
    Icon,
    Table,
    TableCellDropdown,
    ToastContext,
    TooltipHelp,
    Status,
    WeezHeader
} from '@weezevent/nacre';

import { LoadingCard, Paginator, SauronEmptyCard } from 'components';
import IdendityModal from './identityModal';
import { DOMAIN_STATUS } from '../emails.constants';

import { handleSearchQuery } from 'utils/utils';

import css from './identities.module.css';

//TODO Still to be defined backend side
const spec = {
    fields: ['id', 'form_address_domain__verification_status__in', 'search', 'ordering']
};

const initialFilters = {};

export function IdentitiesList({ organizationId }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const history = useHistory();
    const user = usePermissions();
    const isIdentityAdmin = user.hasCapacity('EmailCapacity.IDENTITY_ADMIN', organizationId);
    const isIdentityManager = user.hasCapacity('EmailCapacity.IDENTITY', organizationId);

    const [currentPage, setCurrentPage] = React.useState(0);
    const [defaultPagination] = React.useState(() => ({ limit: 50, offset: 0 }));
    const [pagination, setPagination] = React.useState(() => ({ ...defaultPagination }));
    const [queryParams, setQueryParams] = React.useState(initialFilters);
    const [ordering, setOrdering] = React.useState(initialFilters);
    const [openModal, setOpenModal] = React.useState(false);

    const [identities, loadingIdentities, syncIdentities] = Identity.useApiModel(
        {
            organizationId,
            cache: false,
            allow_cache: false,
            query: {
                limit: pagination.limit,
                offset: pagination.offset,
                ...handleSearchQuery(spec, queryParams, true)
            },
            onError: () => {
                toast.error(t('sauron.toasts.error'));
            }
        },
        [organizationId, pagination, queryParams]
    );

    const [domains] = Domain.useApiModel(
        {
            organizationId,
            cache: false,
            allow_cache: false,
            query: {
                limit: pagination.limit,
                offset: pagination.offset
            },
            onError: () => {
                toast.error(t('sauron.toasts.error'));
            }
        },
        [organizationId, pagination]
    );

    const DNSStatusOptions = [
        { key: 'active', text: t('sauron.emails.identities.dns-status.success'), name: 'success' },
        { key: 'inactive', text: t('sauron.emails.identities.dns-status.failed'), name: 'unstarted, pending, failed' }
    ];
    const defaultSortLabels = [t('sauron.emails.identity.sort.a-z'), t('sauron.emails.identity.sort.z-a')];
    const fields = [
        {
            field: 'id',
            label: t('sauron.emails.identity.id'),
            sortLabels: [t('sauron.emails.identity.sort.id'), t('sauron.emails.identity.sort.-id')]
        },
        {
            field: 'from_address__name',
            label: t('sauron.emails.identity.sender'),
            sortLabels: defaultSortLabels
        },
        {
            field: 'reply_to__name',
            label: t('sauron.emails.identity.reply-to'),
            sortLabels: defaultSortLabels
        }
    ];

    let resetPagination = React.useCallback(() => {
        setPagination({
            ...defaultPagination
        });
        setCurrentPage(0);
    }, []);

    const handleSort = React.useCallback(value => {
        setOrdering(value);
        setQueryParams({ ...queryParams, ordering: value });
        resetPagination();
    });

    let handleSearch = React.useCallback(
        value => {
            const filterSpread = { ...queryParams };
            if (value) {
                filterSpread['search'] = value;
            } else {
                delete filterSpread['search'];
            }
            setQueryParams(filterSpread);
        },
        [queryParams]
    );

    const handlePage = React.useCallback(value => {
        setCurrentPage(value);
        setPagination({ ...pagination, offset: pagination.limit * value });
    }, []);

    const identitiesCount = React.useMemo(() => {
        if (loadingIdentities) {
            return '--';
        }
        return Number.parseInt(identities?.__meta?.headers.get('total-count')) || 0;
    }, [identities, loadingIdentities]);

    const handleDelete = React.useCallback(
        id => {
            const url = `/target/organizations/${organizationId}/identities/${id}`;
            fetcher
                .delete(url)
                .then(response => {
                    if (response.status === 204) {
                        toast.success(t('sauron.toasts.identities.delete-identity-success'));
                    }
                })
                .catch(() => {
                    toast.error(t('sauron.toasts.error'));
                })
                .finally(() => {
                    syncIdentities();
                });
        },
        [syncIdentities]
    );

    return (
        <>
            <WeezHeader
                title={t('sauron.emails.identities.header-title')}
                rightComponent={[
                    (isIdentityAdmin || isIdentityManager) && (
                        <Button
                            key={0}
                            primary
                            icon={<Icon circular={false} inverted={false} margin={false} name="plus" onClick={null} size="regular" />}
                            label={t('sauron.emails.identities.add')}
                            onClick={() => setOpenModal(true)}
                        />
                    )
                ]}
                backLink={{
                    onClick: () => {
                        history.push(`/organizations`);
                    }
                }}
            >
                {t('sauron.emails.identities.header-subtitle')}
            </WeezHeader>
            <Card>
                <ContentCard>
                    <FilterSetController
                        total={identitiesCount}
                        labelTotal={identitiesCount > 1 ? t('common.identities') : t('common.identity')}
                        labelFilters={t('sauron.emails.identities.filter')}
                        labelReset={t('sauron.emails.identities.filters.label-reset')}
                        initialValues={queryParams}
                        defaultValues={initialFilters}
                        onChange={values => {
                            if (queryParams.search !== undefined && queryParams.search.length > 0) {
                                values.search = queryParams.search;
                            } else {
                                delete values.search;
                            }
                            setQueryParams(values);
                        }}
                        onReset={() => setQueryParams(initialFilters)}
                        search={{
                            onChange: (_, value) => handleSearch(value),
                            handleReset: () => handleSearch(''),
                            placeholder: t('sauron.emails.identities.search'),
                            value: queryParams.search
                        }}
                    >
                        <Filter
                            index="1"
                            labelName={t('sauron.emails.identities.dns-status.label')}
                            name="form_address_domain__verification_status__in"
                            options={DNSStatusOptions}
                        />
                    </FilterSetController>
                </ContentCard>
            </Card>
            <Table useNew>
                <Table.THead>
                    <Table.Tr isHeader>
                        {fields.map(({ field, label, sortLabels }, i) => (
                            <SortableHeaders key={i} label={label} field={field} sortLabels={sortLabels} handleSort={handleSort} ordering={ordering} />
                        ))}
                        <th>
                            {t(`sauron.emails.identity.dns-status`)}
                            <TooltipHelp className="" mini={false} position="bottom" title={t('sauron.emails.identity.help-dns-status')} />
                        </th>
                        <th>{t(`sauron.emails.identity.actions.label`)}</th>
                    </Table.Tr>
                </Table.THead>
                <Table.TBody>
                    {!loadingIdentities &&
                        identities?.length > 0 &&
                        identities?.map(_identity => (
                            <IdentityItem
                                key={_identity.id}
                                identity={_identity}
                                organizationId={organizationId}
                                isIdentityAdmin
                                user={user}
                                domain={domains?.filter(d => d.name === _identity.from_address.email.split('@')[1]).map(d => d.verification_status)[0]}
                                onEdit={() => history.push(`identities/edit/${_identity.id}`)}
                                onDelete={() => handleDelete(_identity.id)}
                            />
                        ))}
                </Table.TBody>
            </Table>
            {openModal && (
                <IdendityModal
                    organizationId={organizationId}
                    syncIdentities={syncIdentities}
                    onClose={() => setOpenModal(false)}
                    domains={domains}
                    isIdentityAdmin={isIdentityAdmin}
                    isIdentityManager={isIdentityManager}
                />
            )}
            <LoadingCard loading={loadingIdentities}>
                {identities?.length > 0 ? (
                    <>
                        <Paginator currentPage={currentPage} onPageChange={handlePage} pageSize={pagination.limit} totalCount={identitiesCount} />
                    </>
                ) : (
                    <SauronEmptyCard title={t('sauron.emails.identities.empty-card-title')} subTitle={t('sauron.emails.identities.empty-card-subtitle')} />
                )}
            </LoadingCard>
        </>
    );
}

const SortableHeaders = ({ sortLabels, field, ordering, handleSort, label }) => {
    const [ascLabel, descLabel] = sortLabels;

    return (
        <th>
            <DropdownSort
                active={[field, `-${field}`].includes(ordering)}
                label={label}
                name={field}
                ordering={ordering}
                items={[
                    <DropdownItem.Icons key={`${field}-asc`} item={ascLabel} checked={ordering === field} onClick={() => handleSort(field)} />,
                    <DropdownItem.Icons key={`${field}-desc`} item={descLabel} checked={ordering === `-${field}`} onClick={() => handleSort(`-${field}`)} />
                ]}
            />
        </th>
    );
};
const IdentityItem = ({ identity, organizationId, isIdentityAdmin, domain, onDelete }) => {
    const { t } = useTranslation();
    const history = useHistory();

    const IdentityLink = React.useMemo(() => {
        if (!organizationId) {
            return `/emails/identities/${identity.pk}`;
        }
        return `/organizations/O${organizationId}/emails/identities/${identity.pk}`;
    }, [identity, organizationId]);

    const { statusColor, statusKey } = DOMAIN_STATUS[domain] ?? DOMAIN_STATUS.default;

    return (
        <Table.Tr>
            <Table.Td>
                <div className={css['identity-id']}>
                    <CopyToClipboardMinimal>{identity.id}</CopyToClipboardMinimal>
                </div>
            </Table.Td>
            <Table.Td>
                <div className={css['identity-name']}>{identity.from_address.name}</div>
                {identity.from_address.email}
            </Table.Td>
            <Table.Td>
                <div className={css['identity-name']}>{identity.reply_to.name}</div>
                {identity.reply_to.email}
            </Table.Td>
            <Table.Td>
                <Status color={statusColor} responsive text={t(`sauron.emails.identities.dns-status.${statusKey}`)} />
            </Table.Td>
            <TableCellDropdown>
                <Dropdown.Item item={t('sauron.emails.identity.actions.edit')} onClick={() => history.push(IdentityLink)} />
                {isIdentityAdmin && <Dropdown.Item deleteItem item={t('sauron.emails.identity.actions.delete')} onClick={() => onDelete({ identity })} />}
            </TableCellDropdown>
        </Table.Tr>
    );
};
